import { Box } from "@mui/material";
import { ReactNode, useState, useCallback } from "react";
import JobMap from "../../../../../helpers/JobMap/JobMap";
import { MapToolbar } from "./MapToolbar";
import type { Job } from "../../../../../../models";

export interface MapContainerProps {
	jobs: Job[];
	mapKey: number;
	onClose: () => void;
	height?: string;
	toolbarActions?: ReactNode;
	// Controlled props for feature selection (lifted to parent for Split feature)
	selectedFeatureIds?: Set<string>;
	onSelectedFeaturesChange?: (ids: Set<string>) => void;
}

export function MapContainer({
	jobs,
	mapKey,
	onClose,
	height = "100%",
	toolbarActions,
	selectedFeatureIds: externalSelectedFeatureIds,
	onSelectedFeaturesChange,
}: MapContainerProps) {
	const [lassoMode, setLassoMode] = useState(false);
	// Use internal state if no external control provided (backward compatibility)
	const [internalSelectedFeatureIds, setInternalSelectedFeatureIds] = useState<Set<string>>(new Set());

	// Determine which state to use
	const selectedFeatureIds = externalSelectedFeatureIds ?? internalSelectedFeatureIds;
	const setSelectedFeatureIds = onSelectedFeaturesChange ?? setInternalSelectedFeatureIds;

	const handleToggleLasso = useCallback(() => {
		if (selectedFeatureIds.size > 0) {
			// Clear selection
			setSelectedFeatureIds(new Set());
		} else {
			// Toggle lasso mode
			setLassoMode((prev) => !prev);
		}
	}, [selectedFeatureIds.size, setSelectedFeatureIds]);

	const handleLassoComplete = useCallback(
		(newSelectedIds: Set<string>) => {
			setSelectedFeatureIds(newSelectedIds);
			setLassoMode(false);
		},
		[setSelectedFeatureIds],
	);

	return (
		<Box
			sx={{
				height,
				width: "100%",
				display: "flex",
				flexDirection: "column",
			}}
		>
			<MapToolbar
				jobCount={jobs.length}
				onClose={onClose}
				actions={toolbarActions}
				lassoMode={lassoMode}
				selectedFeatureIds={selectedFeatureIds}
				onToggleLasso={handleToggleLasso}
			/>
			<Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
				<JobMap
					key={mapKey}
					jobs={jobs}
					zoomLevelToDisplayPoints={10}
					height="100%"
					lassoMode={lassoMode}
					selectedFeatureIds={selectedFeatureIds}
					onLassoComplete={handleLassoComplete}
				/>
			</Box>
		</Box>
	);
}
